import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { Waypoint } from 'react-waypoint';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useGetExternalIntegrationsQuery, useLegalMatterFilter } from '../../../../../queries';
import { styleSheet as dbStyleSheet } from '../../../../pages/DataBoards/Home/styles';
import { white } from '../../../../styles/colors';
import { baseStyleSheet } from '../../../../styles/styles';
import { AddBoardItemButton } from '../../../boards/AddBoardItemButton';
import { EditMatterModal } from '../../../dataBoards/matters/EditMatterModal/';
import { dataGridStyles } from '../../../DataGrid/data-grid-styles';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { FolderIcon } from '../../../svgs/icons/FolderIcon';
import { styleSheet } from './styles';

export const EntityMatters = ({ entity }: { entity: Api.IEntity }) => {
	const userSession = useUserSession();
	// Check both v1 and v2 integrations for legal providers
	const { data: externalIntegrations } = useGetExternalIntegrationsQuery({ impersonationContext: undefined });
	const legalV2Providers: Api.IntegrationProvider[] = [
		Api.IntegrationProvider.Clio,
		Api.IntegrationProvider.MyCase,
		Api.IntegrationProvider.PracticePanther,
	];
	const legalV2IntegrationEnabled = externalIntegrations?.some(
		integration => integration.enabled && legalV2Providers.includes(integration.provider)
	);
	const canEdit = !userSession.hasLegalIntegrationConfigured && !legalV2IntegrationEnabled;

	const [isAddModalOpen, setIsAddModalOpen] = React.useState(false);
	const [refreshToken, setRefreshToken] = React.useState(new Date().getTime());
	const matterFilterQuery = useLegalMatterFilter({
		enabled: !!entity.id,
		filterRequest: {
			criteria: [
				{
					property: 'contactId',
					value: entity.id ?? '',
				},
			],
		},
	});

	const matters = React.useMemo(() => {
		return matterFilterQuery.data?.pages.map(page => page.values).flat() ?? [];
	}, [matterFilterQuery.data]);

	const onFetchNextBatchOfMatters = () => {
		matterFilterQuery.fetchNextPage();
	};

	const addItemClicked = () => {
		setIsAddModalOpen(true);
	};

	const refreshMatters = () => {
		setRefreshToken(new Date().getTime());
		setIsAddModalOpen(false);
	};

	const newMatter: Partial<Api.ILegalMatter> = {
		contact: entity as Api.IProjectedContact,
		contactId: entity.id,
	};

	const formatDate = (dateString?: string) => {
		if (!dateString) return '-';
		return moment(dateString).format('MM/DD/YYYY');
	};

	if (matterFilterQuery.isPending) {
		return <LoadingSpinner />;
	}

	return (
		<div key={refreshToken}>
			<div className={css(styleSheet.contentSection)}>
				<header className={css(styleSheet.contentHeader)}>
					<div className={css(dbStyleSheet.boardIconContainer, baseStyleSheet.bgGreen)}>
						<FolderIcon fillColor={white} className={css(styleSheet.matterIcon)} />
					</div>
					<h6 className={css(styleSheet.contentListTitle)}>Matters</h6>
					{canEdit ? (
						<AddBoardItemButton className={css(styleSheet.contentAddButton)} onClick={addItemClicked}>
							Add Matter
						</AddBoardItemButton>
					) : null}
				</header>

				{!matters.length ? <p className={css(styleSheet.emptyText)}>No matters exist for this contact.</p> : null}

				{matters.length ? (
					<>
						<table className={css(dataGridStyles.table, styleSheet.tableOverrides)}>
							<tbody>
								{matters.map(matter => {
									const borderlessRow = matter === matters[0];

									const rowStyles = borderlessRow
										? dataGridStyles.tableDataCellBorderless
										: dataGridStyles.tableDataCell;

									return (
										<tr key={`matter-${matter.id}`}>
											<td className={css(rowStyles)}>
												<span className={css(styleSheet.matterName)}>{matter.matterName}</span>
											</td>
											<td className={css(rowStyles)}>
												<span>{matter.matterNumber}</span>
											</td>
											<td className={css(rowStyles)}>
												<span>{matter.stage?.name || '-'}</span>
											</td>
											<td className={css(rowStyles)}>
												<span>{formatDate(matter.openDate)}</span>
											</td>
											<td className={css(rowStyles)}>
												<span>{formatDate(matter.closeDate)}</span>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
						<Waypoint bottomOffset='-200px' onEnter={onFetchNextBatchOfMatters} />
					</>
				) : null}
			</div>

			{isAddModalOpen ? (
				<EditMatterModal
					initialMatter={newMatter}
					isOpen={isAddModalOpen}
					setIsOpen={setIsAddModalOpen}
					onSave={refreshMatters}
					onCancel={refreshMatters}
				/>
			) : null}
		</div>
	);
};
